/**
 * AuthShell · v8.5.63 · piel — marco compartido de las pantallas de acceso
 * (Login, VerifyMagic, VerifyEmail, ResetPassword): mitad izquierda fluida
 * de portada sobre --paper (oculta bajo 1024px — ahí no entra sin apretar
 * la derecha de 480px) + mitad derecha fija sobre --surface con borde
 * --line, contenido centrado verticalmente. También exporta las piezas de
 * formulario que esas pantallas repiten (campo, input de contraseña,
 * botones, línea de error) para no tener 4 copias del mismo input.
 */
import { Link } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import BrandLockup from "./BrandMark.jsx";

export default function AuthShell({ frase, children }) {
  return (
    <div className="min-h-screen flex bg-[var(--paper)]">
      <div className="hidden lg:flex flex-1 flex-col p-16">
        <Link to="/login" aria-label="Portal Cliente · THE CREW AGENCIA"><BrandLockup/></Link>
        {frase && (
          <div className="flex-1 flex flex-col justify-end pb-16 max-w-xl">
            <p className="font-display text-[32px] leading-[1.1] text-[var(--ink)]">{frase}</p>
          </div>
        )}
      </div>
      <div className="w-full lg:w-[480px] flex-shrink-0 bg-[var(--surface)] lg:rounded-l-[36px] flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-[360px]">
          <div className="lg:hidden mb-8 flex justify-center"><BrandLockup/></div>
          {children}
        </div>
      </div>
    </div>
  );
}

// v8.5.68 · fix auditoría móvil · 16px, no 15px: bajo ese umbral iOS Safari
// hace auto-zoom de toda la página al enfocar el input.
export const INPUT_CLS = "w-full px-4 py-3 bg-[var(--surface)] border border-[var(--line-control)] rounded-[16px] text-[16px] focus:outline-none focus:border-[var(--accent)] text-[var(--ink)] placeholder:text-[var(--faint)]";

export function Campo({ label, children }) {
  return (
    <div>
      <label className="font-mono text-[12px] uppercase tracking-[.08em] text-[var(--muted)] mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}

// v8.5.69 · fix auditoría móvil · autoComplete habilita el autofill/
// sugerencia de Face ID/Touch ID del teléfono — sin esto, el usuario tipea
// todo a mano en cada pantalla de auth. "current-password" por default
// (login); Login.jsx pasa "new-password" explícito en el signup.
export function InputPassword({ value, onChange, showPwd, setShowPwd, placeholder, autoFocus, autoComplete = "current-password" }) {
  return (
    <div className="relative">
      <input type={showPwd ? "text" : "password"} value={value} onChange={e => onChange(e.target.value)} required minLength={8}
             autoComplete={autoComplete} autoFocus={autoFocus} placeholder={placeholder} className={`${INPUT_CLS} pr-10`}/>
      {/* v8.5.69 · fix auditoría móvil · p-2.5 -m-2.5 agranda el área de tap
          (~40px) sin mover el ícono visualmente — antes era solo el ícono
          de 16px, fácil de errar con el dedo. */}
      <button type="button" onClick={() => setShowPwd(s => !s)} aria-label={showPwd ? "Ocultar contraseña" : "Mostrar contraseña"}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-2.5 -m-2.5 text-[var(--faint)] hover:text-[var(--muted)]">
        {showPwd ? <EyeOff className="h-4 w-4"/> : <Eye className="h-4 w-4"/>}
      </button>
    </div>
  );
}

export function BotonPrimario({ submitting, disabled, children, onClick, type = "submit" }) {
  return (
    <button type={type} onClick={onClick} disabled={submitting || disabled}
            className="w-full bg-[var(--ink)] text-[var(--surface)] h-[52px] rounded-full text-[16px] font-extrabold hover:opacity-90 disabled:opacity-40 transition">
      {submitting ? "…" : children}
    </button>
  );
}

export function BotonSecundario({ onClick, children, href }) {
  const cls = "inline-flex items-center h-[44px] px-5 rounded-full bg-[var(--panel)] hover:bg-[var(--line)] text-[var(--ink)] text-sm font-extrabold transition";
  if (href) return <Link to={href} className={cls}>{children}</Link>;
  return <button type="button" onClick={onClick} className={cls}>{children}</button>;
}

export function LineaError({ msg }) {
  return <p className="text-[13px] text-[var(--danger)]">{msg}</p>;
}
